import { collectTranslatableStrings } from "./journeyTranslatableFields";

const isIndex = (segment) => /^\d+$/.test(segment);

/**
 * Immutably sets `value` at dotted `path`, cloning only the containers on the
 * way down. Caller guarantees the path already exists in `node`.
 */
const setAtPath = (node, segments, value) => {
  const [head, ...rest] = segments;
  const clone = Array.isArray(node) ? node.slice() : { ...node };
  const key = Array.isArray(node) && isIndex(head) ? Number(head) : head;
  clone[key] = rest.length === 0 ? value : setAtPath(node[key], rest, value);
  return clone;
};

/**
 * Merge translated strings over an English page.
 *
 * - `stepOverlay` is { "<dotted path>": "<translated string>" }.
 * - A path is applied only if it is a translatable field that exists in the
 *   English page AND the translation is a non-empty string. Anything else
 *   (ids, save_as, enums, URLs, unknown paths) is ignored, so English wins
 *   per field.
 * - Never mutates `page`.
 */
export const applyStringOverlay = (page, stepOverlay) => {
  if (!page || typeof page !== "object") return page;
  if (!stepOverlay || typeof stepOverlay !== "object") return page;

  const translatable = collectTranslatableStrings(page);
  let merged = page;

  Object.keys(stepOverlay).forEach((path) => {
    const translated = stepOverlay[path];
    if (
      !Object.prototype.hasOwnProperty.call(translatable, path) ||
      typeof translated !== "string" ||
      translated.trim() === ""
    ) {
      return;
    }
    merged = setAtPath(merged, path.split("."), translated);
  });

  return merged;
};

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getOverlay = (overlays, slug, lang) =>
  overlays?.[normalize(slug)]?.[normalize(lang)] || null;

/**
 * Localize an authored page for (slug, lang). Unknown slug/lang/step, or lang
 * "en", returns the English page untouched.
 */
export const localizeJourneyPage = ({
  overlays,
  journeySlug,
  stepId,
  lang,
  page,
}) => {
  if (!page || normalize(lang) === "en") return page;
  const overlay = getOverlay(overlays, journeySlug, lang);
  const stepOverlay = overlay?.[String(stepId || "")];
  return applyStringOverlay(page, stepOverlay);
};

/** Translated step-list title from the overlay's __steps__ block, or null. */
export const getLocalizedStepTitle = ({
  overlays,
  journeySlug,
  stepId,
  lang,
}) => {
  if (normalize(lang) === "en") return null;
  const title =
    getOverlay(overlays, journeySlug, lang)?.__steps__?.[String(stepId || "")]
      ?.title;
  return typeof title === "string" && title.trim() !== "" ? title : null;
};
